import { GeoJSONSource } from "mapbox-gl";
import MapboxViewer from "@/components/mapbox/MapboxViewer";
import { MapboxPolygonObject } from "../Objects/MapboxPolygonObject";
import { autobind } from "core-decorators";
import { MAPBOX_MIN_ZOOM } from "@/constant";

class PolygonEntity {
  private _mapboxViewer: MapboxViewer;
  private _records: Map<string, MapboxPolygonObject> = new Map();
  public sourceId = "polygon-source";
  public vertexSourceId = "polygon-vertex-source";
  public offsetSourceId = "polygon-offset-source";
  public clearanceSourceId = "polygon-clearance-source";
  public clearanceLineSourceId = "polygon-clearance-line-source";
  public source: GeoJSONSource | undefined = undefined;
  public vertexSource: GeoJSONSource | undefined = undefined;
  public offsetSource: GeoJSONSource | undefined = undefined;
  public clearanceSource: GeoJSONSource | undefined = undefined;
  public clearanceLineSource: GeoJSONSource | undefined = undefined;
  public geojson: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: [],
  };
  public vertexGeojson: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: [],
  };
  public offsetGeojson: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: [],
  };

  public clearanceGeojson: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: [],
  };

  public clearanceLineGeojson: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: [],
  };
  private _savedLayerVisibility = new Map<string, string>();
  private readonly _layerIds = [
    "polygon-fill",
    "polygon-extrusion",
    "polygon-outline",
    "polygon-offset-line",
    "polygon-clearance-fill",
    "polygon-clearance-lines",
    "polygon-vertices",
    "polygon-midpoints",
  ];

  constructor(mapboxViewer: MapboxViewer) {
    this._mapboxViewer = mapboxViewer;
    this.init();
  }

  @autobind
  init() {
    // Create polygon source
    this.dispose();
    this._mapboxViewer._mapViewer.addSource(this.sourceId, {
      type: "geojson",
      data: this.geojson,
      minzoom: MAPBOX_MIN_ZOOM,
    });

    // Create vertex source
    this._mapboxViewer._mapViewer.addSource(this.vertexSourceId, {
      type: "geojson",
      data: this.vertexGeojson,
      minzoom: MAPBOX_MIN_ZOOM,
    });

    // Create offset line source
    this._mapboxViewer._mapViewer.addSource(this.offsetSourceId, {
      type: "geojson",
      data: this.offsetGeojson,
      minzoom: MAPBOX_MIN_ZOOM,
    });

    // Create clearance area source
    this._mapboxViewer._mapViewer.addSource(this.clearanceSourceId, {
      type: "geojson",
      data: this.clearanceGeojson,
      minzoom: MAPBOX_MIN_ZOOM,
    });

    // Create clearance line source
    this._mapboxViewer._mapViewer.addSource(this.clearanceLineSourceId, {
      type: "geojson",
      data: this.clearanceLineGeojson,
      minzoom: MAPBOX_MIN_ZOOM,
    });

    // Add fill layer with visibility filter (only for non-extruded polygons)
    this._mapboxViewer._mapViewer.addLayer({
      id: "polygon-fill",
      type: "fill",
      source: this.sourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "visible"], true], // Add visibility filter
        // ["<=", ["get", "height"], 0], // Only show 2D fill when height <= 0
      ],
      paint: {
        "fill-color": [
          "case",
          [
            "any",
            ["boolean", ["feature-state", "isViolatingRestrictions"], false],
            ["get", "isViolatingRestrictions"],
          ],
          "#ff4444", // Red when violating restrictions
          ["boolean", ["feature-state", "dragging"], false],
          "#ff9900", // Orange when dragging
          ["get", "color"],
        ],
        "fill-opacity": [
          "case",
          [
            "any",
            ["boolean", ["feature-state", "isViolatingRestrictions"], false],
            ["get", "isViolatingRestrictions"],
          ],
          0.6, // Higher opacity when violating
          ["boolean", ["feature-state", "dragging"], false],
          0.7, // Higher opacity when dragging
          ["boolean", ["feature-state", "selected"], false],
          0.5,
          ["boolean", ["feature-state", "highlight"], false],
          0.4,
          0.3,
        ],
      },
    });

    // Add 3D extrusion layer with visibility filter
    this._mapboxViewer._mapViewer.addLayer({
      id: "polygon-extrusion",
      type: "fill-extrusion",
      source: this.sourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "visible"], true], // Add visibility filter
        // [">", ["get", "height"], 0], // Only extrude if height > 0
      ],
      paint: {
        "fill-extrusion-color": [
          "case",
          [
            "any",
            ["boolean", ["feature-state", "isViolatingRestrictions"], false],
            ["get", "isViolatingRestrictions"],
          ],
          "#ff4444", // Red when violating
          ["boolean", ["feature-state", "dragging"], false],
          "#ff9900", // Orange when dragging
          ["get", "color"],
        ],
        "fill-extrusion-height": ["get", "height"],
        "fill-extrusion-base": ["get", "base_height"],
        "fill-extrusion-opacity": 0.6,
        //  [
        //   "case",
        //   ["boolean", ["feature-state", "dragging"], false],
        //   0.8, // Higher opacity when dragging
        //   ["boolean", ["feature-state", "selected"], false],
        //   0.7,
        //   ["boolean", ["feature-state", "highlight"], false],
        //   0.6,
        //   0.5,
        // ],
      },
    });

    // Add outline layer with visibility filter
    this._mapboxViewer._mapViewer.addLayer({
      id: "polygon-outline",
      type: "line",
      source: this.sourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "visible"], true], // Add visibility filter
      ],
      paint: {
        "line-color": [
          "case",
          [
            "any",
            ["boolean", ["feature-state", "isViolatingRestrictions"], false],
            ["get", "isViolatingRestrictions"],
          ],
          "#ff0000", // Bright red when violating
          ["boolean", ["feature-state", "dragging"], false],
          "#ff6600", // Orange when dragging
          ["get", "color"],
        ],
        "line-width": [
          "case",
          [
            "any",
            ["boolean", ["feature-state", "isViolatingRestrictions"], false],
            ["get", "isViolatingRestrictions"],
          ],
          4, // Thicker when violating
          ["boolean", ["feature-state", "dragging"], false],
          4, // Thicker when dragging
          ["boolean", ["feature-state", "selected"], false],
          3,
          ["boolean", ["feature-state", "highlight"], false],
          2.5,
          2,
        ],
        "line-dasharray": [
          "case",
          ["boolean", ["feature-state", "dragging"], false],
          [5, 3], // Dashed when dragging
          [1, 0], // Solid line normally
        ],
      },
    });

    // Add offset line layer (render on top of 3D extrusions)
    this._mapboxViewer._mapViewer.addLayer({
      id: "polygon-offset-line",
      type: "line",
      source: this.offsetSourceId,
      slot: "top",
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "visible"], true],
        [
          "any",
          [">", ["get", "offsetDistance"], 0],
          ["==", ["get", "isManualOffset"], true],
        ],
      ],
      paint: {
        "line-color": [
          "case",
          [
            "any",
            ["boolean", ["feature-state", "isViolatingRestrictions"], false],
            ["get", "isViolatingRestrictions"],
          ],
          "#ff0000",
          ["get", "color"],
        ],
        "line-width": 3,
        "line-dasharray": [3, 2],
        "line-opacity": 0.9,
      },
    });

    // Add clearance area fill layer (ring between polygon and clearance buffer)
    this._mapboxViewer._mapViewer.addLayer({
      id: "polygon-clearance-fill",
      type: "fill",
      source: this.clearanceSourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "visible"], true],
        [">", ["get", "clearanceDistance"], 0],
        ["==", ["get", "showClearanceArea"], true],
      ],
      paint: {
        "fill-color": ["get", "color"],
        "fill-opacity": 0.15,
      },
    });

    // Add clearance lines (offset edges and perpendicular caps)
    this._mapboxViewer._mapViewer.addLayer({
      id: "polygon-clearance-lines",
      type: "line",
      source: this.clearanceLineSourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "visible"], true],
        [">", ["get", "clearanceDistance"], 0],
        ["==", ["get", "showClearanceArea"], true],
      ],
      paint: {
        "line-color": ["get", "color"],
        "line-width": 2,
        "line-opacity": 0.9,
      },
    });

    // Add vertex layers with visibility filter
    this._mapboxViewer._mapViewer.addLayer({
      id: "polygon-vertices",
      type: "circle",
      source: this.vertexSourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "vertexType"], "corner"],
        ["==", ["get", "visible"], true], // Add visibility filter
      ],
      layout: {
        visibility: "none",
      },
      paint: {
        "circle-radius": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          8,
          ["boolean", ["feature-state", "hover"], false],
          7,
          6,
        ],
        "circle-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          "#ff4444",
          ["boolean", ["feature-state", "hover"], false],
          "#ff6666",
          "#ffffff",
        ],
        "circle-stroke-color": "#333333",
        "circle-stroke-width": 2,
      },
    });

    // Add midpoint vertices layer with visibility filter
    this._mapboxViewer._mapViewer.addLayer({
      id: "polygon-midpoints",
      type: "circle",
      source: this.vertexSourceId,
      filter: [
        "all",
        ["==", ["get", "app"], "logistic-app"],
        ["==", ["get", "vertexType"], "midpoint"],
        ["==", ["get", "visible"], true], // Add visibility filter
      ],
      layout: {
        visibility: "none", // Hidden by default
      },
      paint: {
        "circle-radius": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          6,
          4,
        ],
        "circle-color": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          "#00ff00",
          "#88ff88",
        ],
        "circle-stroke-color": "#333333",
        "circle-stroke-width": 1,
        "circle-opacity": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          1.0,
          0.7,
        ],
      },
    });

    this.source = this._mapboxViewer._mapViewer.getSource(
      this.sourceId
    ) as GeoJSONSource;
    this.vertexSource = this._mapboxViewer._mapViewer.getSource(
      this.vertexSourceId
    ) as GeoJSONSource;
    this.offsetSource = this._mapboxViewer._mapViewer.getSource(
      this.offsetSourceId
    ) as GeoJSONSource;
    this.clearanceSource = this._mapboxViewer._mapViewer.getSource(
      this.clearanceSourceId
    ) as GeoJSONSource;
    this.clearanceLineSource = this._mapboxViewer._mapViewer.getSource(
      this.clearanceLineSourceId
    ) as GeoJSONSource;
  }

  @autobind
  add(polygon: MapboxPolygonObject) {
    this._records.set(polygon.contentId, polygon);
    this.updateSource();
  }

  @autobind
  remove(contentId: string) {
    this._records.delete(contentId);
    this.updateSource();
  }

  @autobind
  updateSource() {
    // Update polygon features
    this.geojson.features = Array.from(this._records.values())
      .map((polygon) => polygon.getFeature())
      .filter((feature) => feature !== null) as GeoJSON.Feature[];

    // Update vertex features
    this.vertexGeojson.features = Array.from(this._records.values())
      .flatMap((polygon) => polygon.getVertexFeatures())
      .filter((feature) => feature !== null) as GeoJSON.Feature[];

    // Update offset line features
    this.offsetGeojson.features = Array.from(this._records.values())
      .map((polygon) => polygon.getOffsetLineFeature?.())
      .filter((feature) => feature !== null) as GeoJSON.Feature[];

    // Update clearance area features
    this.clearanceGeojson.features = Array.from(this._records.values())
      .map((polygon) => polygon.getClearanceAreaFeature?.())
      .filter((feature) => feature !== null) as GeoJSON.Feature[];

    // Update clearance line features
    this.clearanceLineGeojson.features = Array.from(this._records.values())
      .flatMap((polygon) => polygon.getClearanceLinesFeatures?.() || [])
      .filter((feature) => feature !== null) as GeoJSON.Feature[];

    this.source?.setData(this.geojson);
    this.vertexSource?.setData(this.vertexGeojson);
    this.offsetSource?.setData(this.offsetGeojson);
    this.clearanceSource?.setData(this.clearanceGeojson);
    this.clearanceLineSource?.setData(this.clearanceLineGeojson);
  }

  @autobind
  showVertices() {
    this._mapboxViewer._mapViewer.setLayoutProperty(
      "polygon-vertices",
      "visibility",
      "visible"
    );
    this._mapboxViewer._mapViewer.setLayoutProperty(
      "polygon-midpoints",
      "visibility",
      "visible"
    );
  }

  @autobind
  hideVertices() {
    this._mapboxViewer._mapViewer.setLayoutProperty(
      "polygon-vertices",
      "visibility",
      "none"
    );
    this._mapboxViewer._mapViewer.setLayoutProperty(
      "polygon-midpoints",
      "visibility",
      "none"
    );
  }

  /** Hide polygon layers while the simshady mesh overlay is shown. */
  @autobind
  setShadingOverlayActive(active: boolean) {
    const map = this._mapboxViewer._mapViewer;

    if (active) {
      for (const layerId of this._layerIds) {
        if (!map.getLayer(layerId)) continue;
        this._savedLayerVisibility.set(
          layerId,
          map.getLayoutProperty(layerId, "visibility") as string,
        );
        map.setLayoutProperty(layerId, "visibility", "none");
      }
      return;
    }

    for (const layerId of this._layerIds) {
      if (!map.getLayer(layerId)) continue;
      const saved = (this._savedLayerVisibility.get(layerId) ?? "visible") as
        | "none"
        | "visible";
      map.setLayoutProperty(layerId, "visibility", saved);
    }
    this._savedLayerVisibility.clear();
  }

  @autobind
  getById(contentId: string) {
    return this._records.get(contentId);
  }

  @autobind
  dispose() {
    // Remove layers and sources
    const layers = [
      "polygon-fill",
      "polygon-extrusion",
      "polygon-outline",
      "polygon-offset-line",
      "polygon-clearance-fill",
      "polygon-clearance-lines",
      "polygon-vertices",
      "polygon-midpoints",
    ];
    layers.forEach((layerId) => {
      if (this._mapboxViewer._mapViewer.getLayer(layerId)) {
        this._mapboxViewer._mapViewer.removeLayer(layerId);
      }
    });

    const sources = [
      this.sourceId,
      this.vertexSourceId,
      this.offsetSourceId,
      this.clearanceSourceId,
      this.clearanceLineSourceId,
    ];
    sources.forEach((sourceId) => {
      if (this._mapboxViewer._mapViewer.getSource(sourceId)) {
        this._mapboxViewer._mapViewer.removeSource(sourceId);
      }
    });

    this._records.clear();
  }
}

export default PolygonEntity;
